import {
  ButtonIcon,
  Menu,
  MenuItem,
  MenuTrigger,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import {
  RiBugLine,
  RiCloseCircleLine,
  RiFileCopyLine,
  RiMore2Line,
} from '@remixicon/react';
import { alertApiRef, errorApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import { catalogEntityDeletePermission } from '@backstage/plugin-catalog-common/alpha';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';

type EntityContextMenuProps = {
  onUnregisterEntity: () => void;
  onInspectEntity: () => void;
};

/**
 * The entity page's "More actions" menu on BUI, with the items of
 * @backstage/plugin-catalog's legacy EntityContextMenu: Unregister entity
 * (disabled without the catalog delete permission), Inspect entity and
 * Copy entity URL.
 */
export const EntityContextMenu = ({
  onUnregisterEntity,
  onInspectEntity,
}: EntityContextMenuProps) => {
  const { t } = useTranslationRef(catalogTranslationRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const unregisterPermission = useEntityPermission(
    catalogEntityDeletePermission,
  );

  const copyUrl = () => {
    window.navigator.clipboard.writeText(window.location.toString()).then(
      () =>
        alertApi.post({
          message: t('entityContextMenu.copiedMessage'),
          severity: 'info',
          display: 'transient',
        }),
      error => errorApi.post(error),
    );
  };

  return (
    <MenuTrigger>
      <TooltipTrigger>
        <ButtonIcon
          variant="secondary"
          icon={<RiMore2Line />}
          aria-label={t('entityContextMenu.moreButtonAriaLabel')}
        />
        <Tooltip>{t('entityContextMenu.moreButtonTitle')}</Tooltip>
      </TooltipTrigger>
      <Menu placement="bottom end">
        <MenuItem
          iconStart={<RiCloseCircleLine />}
          isDisabled={!unregisterPermission.allowed}
          // BUI's MenuItem has no disabled look; grey it out as the legacy
          // menu did, so it doesn't read as clickable.
          style={
            unregisterPermission.allowed
              ? undefined
              : { color: 'var(--bui-fg-disabled)' }
          }
          onAction={onUnregisterEntity}
        >
          {t('entityContextMenu.unregisterMenuTitle')}
        </MenuItem>
        <MenuItem iconStart={<RiBugLine />} onAction={onInspectEntity}>
          {t('entityContextMenu.inspectMenuTitle')}
        </MenuItem>
        <MenuItem iconStart={<RiFileCopyLine />} onAction={copyUrl}>
          {t('entityContextMenu.copyURLMenuTitle')}
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
};
